// FIN-984/FIN-998: the one rule for what a tax-grant poll answer means, shared by the client
// app's TaxConnectSheet and src/'s TaxAccountSection. Pure, with its own subpath export, so src/
// can take it without depending on this package's React components (same as feedback/core).

/** What the capture-redirect recorded for the latest trip to the authority's e-service. */
export type TaxGrantRecordedOutcome = "granted" | "cancelled"

/**
 * Where a connect attempt stands, as the sheet shows it.
 *
 * - `active`: the roles lookup says the grant is live (screens §2c).
 * - `insufficient_scope`: signed, but without the role that reads the account.
 * - `signed`: the user came back saying they signed, and the authority has not activated it yet
 *   (§2d). Display-only — the account is still `pending`, and only the roles lookup promotes it.
 * - `declined`: the user cancelled at the authority (§2e).
 * - `pending`: not back yet. Opening the link proves nothing, so this is the default.
 */
export type TaxGrantStage = "active" | "insufficient_scope" | "signed" | "declined" | "pending"

/**
 * The account's status decides first; the recorded outcome only refines a `pending` account.
 *
 * Status first because it is the authority's own answer: a cancel recorded by an old tab cannot
 * outvote a grant the roles lookup says is live, and a partial grant already names its own next
 * step. Every other status (`draft`, `revoked`, `closed`) keeps the sheet waiting, as before.
 */
export function taxGrantStage(status: string, grantOutcome?: TaxGrantRecordedOutcome | null): TaxGrantStage {
  if (status === "active") return "active"
  if (status === "insufficient_scope") return "insufficient_scope"
  if (status !== "pending") return "pending"
  if (grantOutcome === "cancelled") return "declined"
  if (grantOutcome === "granted") return "signed"
  return "pending"
}

/** Why the first reading after a grant ended the journey without a connection to show. */
export type TaxConnectRefusal = "closed" | "revoked"

/**
 * The refusal a first reading's unavailable reason settles, if any. `closed` is final (FIN-1035);
 * `revoked` means the grant did not give us access, so the journey offers to sign again (FIN-1111).
 */
export function taxConnectRefusal(balanceUnavailableReason: string | null | undefined): TaxConnectRefusal | undefined {
  return balanceUnavailableReason === "closed" || balanceUnavailableReason === "revoked"
    ? balanceUnavailableReason
    : undefined
}
